/**
 * Copyright © 2018 Emu Analytics
 */

import { Client } from './client';
import { IBaseResource, IQuery } from './api-interfaces';

export class BaseEndpoints<T extends IBaseResource> {
  constructor(protected readonly client: Client, private readonly resourcePath: string) {}

  protected get resourceEndpoint(): string {
    return `/api/v1/${this.resourcePath}`;
  }

  list(params?: IQuery): Promise<T[]> {
    // Reformat filter for query string
    if (params?.filter) {
      const filter = params.filter;
      Object.keys(filter).forEach(key => {
        (params as any)[`filter[${key}]`] = filter[key];
      });
      delete params.filter;
    }

    return this.client._get(this.resourceEndpoint, { params });
  }

  get(id: string, params: Record<string, any> = {}): Promise<T> {
    return this.client._get(`${this.resourceEndpoint}/${id}`, { params });
  }

  create(data: Partial<T>, params: Record<string, any> = {}): Promise<T> {
    return this.client._post(`${this.resourceEndpoint}`, data, { params });
  }

  update(id: string, data: Partial<T>, params: Record<string, any> = {}): Promise<T> {
    return this.client._put(`${this.resourceEndpoint}/${id}`, data, { params });
  }

  delete(id: string, params: Record<string, any> = {}) {
    return this.client._delete(`${this.resourceEndpoint}/${id}`, { params });
  }

  getMetadata(id: string, key?: string, params: Record<string, any> = {}): Promise<any> {
    const endpoint = key
      ? `${this.resourceEndpoint}/${id}/metadata/${key}`
      : `${this.resourceEndpoint}/${id}/metadata`;
    return this.client._get(endpoint, { params });
  }

  setMetadata(id: string, key: string, value: any, params: Record<string, any> = {}): Promise<any> {
    // Wrap primitives in value object
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      value = { value };
    }

    return this.client._put(`${this.resourceEndpoint}/${id}/metadata/${key}`, value, {
      headers: {
        'Content-Type': 'application/json'
      },
      params
    });
  }

  deleteMetadata(id: string, key: string, params: Record<string, any> = {}): Promise<any> {
    return this.client._delete(`${this.resourceEndpoint}/${id}/metadata/${key}`, {
      headers: {
        'Content-Type': 'application/json'
      },
      params
    });
  }
}
